import * as T from 'three';
import {clone as cloneSkeleton} from 'three/addons/utils/SkeletonUtils.js';
import {PORTAL_DEFAULTS, portalEntryFrame, portalToLocal, resolvePortalPair, selectedPortals, portalDestinationSide} from './portal-math.mjs';

const FLIP = new T.Matrix4().makeRotationY(Math.PI);
const materialList = material => Array.isArray(material) ? material : [material];
const sharedBoneBounds = new WeakMap();
const monotonicNow = () => globalThis.performance?.now?.() ?? Date.now();
function createPreparationQueue({scheduleIdle, budgetMs = 2} = {}) {
  const tasks = [], stats = {slices: 0, totalMs: 0, maxSliceMs: 0, maxStepMs: 0, pending: 0};
  const schedule = scheduleIdle ?? (callback => typeof globalThis.requestIdleCallback === 'function'
    ? globalThis.requestIdleCallback(callback, {timeout: 100})
    : setTimeout(() => callback({timeRemaining: () => budgetMs}), 16));
  let scheduled = false;
  function run(deadline) {
    scheduled = false;
    const start = monotonicNow(), allowance = Math.max(0, Math.min(budgetMs, deadline?.timeRemaining?.() ?? budgetMs));
    let steps = 0;
    while (tasks.length && (steps === 0 || monotonicNow() - start < allowance)) {
      const task = tasks[0], stepStart = monotonicNow();
      try { if (task.step()) { tasks.shift(); task.resolve(); } }
      catch (error) { tasks.shift(); task.reject(error); }
      stats.maxStepMs = Math.max(stats.maxStepMs, monotonicNow() - stepStart);
      steps++;
    }
    const elapsed = monotonicNow() - start;
    stats.slices++; stats.totalMs += elapsed; stats.maxSliceMs = Math.max(stats.maxSliceMs, elapsed); stats.pending = tasks.length;
    kick();
  }
  function kick() { if (!scheduled && tasks.length) { scheduled = true; schedule(run); } }
  return {
    add(step) { const promise = new Promise((resolve, reject) => tasks.push({step, resolve, reject})); stats.pending = tasks.length; kick(); return promise; },
    stats: () => ({...stats}),
  };
}
function frame(portal) {
  return new T.Matrix4().makeRotationY(portal.yaw ?? 0).setPosition(portal.position.x, portal.position.y, portal.position.z);
}
export function portalObjectMatrix(matrixWorld, source, target, result = new T.Matrix4()) {
  return result.copy(frame(target)).multiply(FLIP).multiply(frame(source).invert()).multiply(matrixWorld);
}
export function portalHalfSpace(portal, side = 1) {
  const normal = new T.Vector3(Math.sin(portal.yaw ?? 0), 0, Math.cos(portal.yaw ?? 0)).multiplyScalar(side);
  return new T.Plane().setFromNormalAndCoplanarPoint(normal, new T.Vector3(portal.position.x, portal.position.y, portal.position.z));
}
function dimensions(source, target) {
  return {width: Math.min(source.width ?? PORTAL_DEFAULTS.width, target.width ?? PORTAL_DEFAULTS.width), height: Math.min(source.height ?? PORTAL_DEFAULTS.height, target.height ?? PORTAL_DEFAULTS.height), arch: source.aperture === 'arch' || target.aperture === 'arch'};
}
/** Inward aperture planes. Rectangles are exact; authored arches use a bounded
 * 16-segment inscribed cap. Keeping the aperture in the clip volume prevents an
 * outstretched hand outside the doorway from disappearing through a wall.
 */
export function portalAperturePlanes(portal, shape, archSegments = 16) {
  const {width, height, arch} = shape, half = width / 2;
  const polygon = [new T.Vector2(-half, 0), new T.Vector2(half, 0)];
  if (arch) {
    const radius = Math.min(half, height), spring = height - radius;
    for (let i = 0; i <= archSegments; i++) {
      const angle = i / archSegments * Math.PI;
      polygon.push(new T.Vector2(Math.cos(angle) * half, spring + Math.sin(angle) * radius));
    }
  } else polygon.push(new T.Vector2(half, height), new T.Vector2(-half, height));
  const matrix = frame(portal), planes = [];
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i], b = polygon[(i + 1) % polygon.length], dx = b.x - a.x, dy = b.y - a.y;
    if (Math.hypot(dx, dy) < 1e-8) continue;
    const normal = new T.Vector3(-dy, dx, 0).normalize();
    planes.push(new T.Plane().setFromNormalAndCoplanarPoint(normal, new T.Vector3(a.x, a.y, 0)).applyMatrix4(matrix));
  }
  return planes;
}
export function straddlingClipVolumes(source, target, side) {
  const shape = dimensions(source, target);
  // Source removes the INTERSECTION of beyond-plane and inside-aperture.
  // Destination keeps the intersection of emerged-halfspace and aperture.
  return {
    source: {planes: [portalHalfSpace(source, side), ...portalAperturePlanes(source, shape).map(plane => plane.negate())], intersection: true},
    target: {planes: [portalHalfSpace(target, side), ...portalAperturePlanes(target, shape)], intersection: false},
  };
}
export function clippingKeepsPoint(volume, point) {
  const outside = volume.planes.map(plane => plane.distanceToPoint(point) < -1e-9);
  return volume.intersection ? !outside.every(Boolean) : !outside.some(Boolean);
}
function applyClip(material, volume) {
  const changed = material.clippingPlanes?.length !== volume.planes.length || material.clipIntersection !== volume.intersection;
  material.clippingPlanes = volume.planes;
  material.clipIntersection = volume.intersection;
  material.clipShadows = true;
  if (changed) material.needsUpdate = true;
}
function cloneMaterial(original) {
  const material = original.clone();
  material.onBeforeCompile = original.onBeforeCompile;
  material.customProgramCacheKey = original.customProgramCacheKey;
  material.userData.portalOriginalVersion = original.version;
  return material;
}
function copyMaterialState(copy, original) {
  // Most appearance changes (avatar customization, pulses, transparency) do
  // not increment Material.version. Copy these every frame without allocating
  // or globally changing the original. Shader uniforms remain shared read-only.
  if (copy.userData.portalOriginalVersion !== original.version) {
    copy.copy(original);
    copy.onBeforeCompile = original.onBeforeCompile;
    copy.customProgramCacheKey = original.customProgramCacheKey;
    copy.userData.portalOriginalVersion = original.version;
    copy.needsUpdate = true;
  }
  for (const key of ['color', 'emissive', 'specular', 'normalScale']) if (copy[key]?.copy && original[key]) copy[key].copy(original[key]);
  for (const key of ['opacity', 'transparent', 'alphaTest', 'roughness', 'metalness', 'emissiveIntensity', 'visible', 'depthWrite', 'depthTest', 'side', 'wireframe', 'map', 'alphaMap', 'normalMap', 'emissiveMap', 'roughnessMap', 'metalnessMap']) if (key in original) copy[key] = original[key];
  if (original.isShaderMaterial) copy.uniforms = original.uniforms;
}
function supportedRoot(root) {
  let supported = true, found = false;
  root.traverse(object => {
    if (!object.material) return;
    found = true;
    if (object.isInstancedMesh || object.isBatchedMesh || object.customDepthMaterial || object.customDistanceMaterial) supported = false;
    for (const material of materialList(object.material)) {
      if (!material || material.isRawShaderMaterial || material.isShaderMaterial || material.clippingPlanes?.length) supported = false;
    }
  });
  return found && supported;
}
function updateSourceMatrices(root) {
  root.updateWorldMatrix(true, false);
  // SkinnedMesh updates bindMatrixInverse in updateMatrixWorld (not the
  // similarly named updateWorldMatrix). Run that override before posed bounds.
  root.updateMatrixWorld(true);
}
function captureLayout(root) {
  const layout = [];
  root.traverse(object => layout.push({object, geometry: object.geometry, boundsVersion: object.geometry ? boundsKey(object) : null, materials: object.material ? [...materialList(object.material)] : null}));
  return layout;
}
function layoutChanged(handle) {
  let index = 0, changed = false;
  const bases = handle.presentation?.bindings;
  handle.root.traverse(object => {
    const prior = handle.layout[index++], binding = bases?.get(object);
    const material = binding && object.material === binding.sourceMaterial ? binding.baseMaterial : object.material;
    const materials = material ? materialList(material) : null;
    if (!prior || prior.object !== object || prior.geometry !== object.geometry || prior.boundsVersion !== (object.geometry ? boundsKey(object) : null) || prior.materials?.length !== materials?.length || materials?.some((item, i) => item !== prior.materials[i])) changed = true;
  });
  return changed || index !== handle.layout.length;
}
function broadRadius(root, suppliedRadius) {
  if (Number.isFinite(suppliedRadius) && suppliedRadius > 0) return suppliedRadius;
  updateSourceMatrices(root);
  const box = new T.Box3().setFromObject(root).applyMatrix4(root.matrixWorld.clone().invert());
  if (box.isEmpty()) return 1;
  const radius = Math.max(box.min.length(), box.max.length());
  return Math.max(.5, radius + .6); // conservative room for animated limbs
}
function boundsKey(mesh) {
  const geometry = mesh.geometry;
  return [geometry.uuid, geometry.attributes.position?.version, geometry.attributes.skinWeight?.version, geometry.attributes.skinIndex?.version, geometry.morphTargetsRelative, ...(geometry.morphAttributes.position ?? []).map(attribute => attribute.version)].join(':');
}
function boneBoundsBuilder(mesh) {
  const {geometry, skeleton} = mesh, positions = geometry.attributes.position, indices = geometry.attributes.skinIndex, weights = geometry.attributes.skinWeight, morphs = geometry.morphAttributes.position ?? [];
  const record = {key: boundsKey(mesh), done: false, boxes: null, cursor: 0, morphCursor: 0, morphBoxes: null, deltaTransforms: null, promise: null};
  if (!positions || !indices || !weights || morphs.some(attribute => attribute.count !== positions.count)) { record.done = true; return record; }
  record.boxes = skeleton.bones.map(() => new T.Box3());
  record.morphBoxes = morphs.map(() => new T.Box3());
  if (morphs.length) record.deltaTransforms = skeleton.boneInverses.map(inverse => new T.Matrix4().multiplyMatrices(inverse, mesh.bindMatrix).setPosition(0, 0, 0));
  const position = new T.Vector3(), local = new T.Vector3();
  record.step = (chunk = 256) => {
    const end = Math.min(positions.count, record.cursor + chunk), baseStart = record.cursor;
    for (; record.cursor < end; record.cursor++) {
      const vertex = record.cursor;
      position.fromBufferAttribute(positions, vertex).applyMatrix4(mesh.bindMatrix);
      let total = 0;
      for (let influence = 0; influence < 4; influence++) {
        const weight = weights.getComponent(vertex, influence), index = indices.getComponent(vertex, influence);
        if (weight < 0 || !Number.isFinite(weight)) { record.boxes = null; record.done = true; record.step = null; return true; }
        total += weight;
        if (weight === 0) continue;
        if (!record.boxes[index] || !skeleton.boneInverses[index]) { record.boxes = null; record.done = true; record.step = null; return true; }
        local.copy(position).applyMatrix4(skeleton.boneInverses[index]); record.boxes[index].expandByPoint(local);
      }
      if (Math.abs(total - 1) > 1e-4) { record.boxes = null; record.done = true; record.step = null; return true; }
    }
    // Morph deltas are geometry-space AABBs, independent of bone assignment.
    // Each target/vertex sample consumes one job unit, so 41 facial targets
    // do not multiply a nominal 256-vertex job into an unbounded cold scan.
    const morphEnd = Math.min(positions.count * morphs.length, record.morphCursor + chunk - (record.cursor - baseStart));
    for (; record.morphCursor < morphEnd; record.morphCursor++) {
      const target = Math.floor(record.morphCursor / positions.count), vertex = record.morphCursor % positions.count;
      local.fromBufferAttribute(morphs[target], vertex);
      if (!geometry.morphTargetsRelative) local.sub(position.fromBufferAttribute(positions, vertex));
      record.morphBoxes[target].expandByPoint(local);
    }
    record.done = record.cursor === positions.count && record.morphCursor === positions.count * morphs.length;
    if (record.done) record.step = null; // release the source mesh captured during preparation
    return record.done;
  };
  return record;
}
function sharedBoundsRecord(mesh) {
  let bySkeleton = sharedBoneBounds.get(mesh.geometry);
  if (!bySkeleton) { bySkeleton = new WeakMap(); sharedBoneBounds.set(mesh.geometry, bySkeleton); }
  let byBind = bySkeleton.get(mesh.skeleton.boneInverses);
  if (!byBind) { byBind = new Map(); bySkeleton.set(mesh.skeleton.boneInverses, byBind); }
  const key = boundsKey(mesh) + ':' + mesh.bindMatrix.elements.join(',');
  let record = byBind.get(key);
  if (!record) { record = boneBoundsBuilder(mesh); byBind.set(key, record); }
  return record;
}
function makeBoneInfluenceBounds(mesh) {
  const record = sharedBoundsRecord(mesh);
  while (!record.done) record.step();
  return record;
}
/** Each skinned vertex is a convex combination of transformed bone-influence
 * points. The union of their AABBs therefore conservatively contains every
 * deformed vertex. This visits vertices once, then only bones each frame.
 * Morph envelopes add weighted delta AABBs before the same bone transforms.
 * Unnormalized skin data alone falls back to exact vertices.
 */
export function visualWorldBounds(root, cache = new WeakMap(), {allowBuild = true} = {}) {
  updateSourceMatrices(root);
  const result = new T.Box3(), transformed = new T.Box3(), morphed = new T.Box3(), delta = new T.Box3(), boneDelta = new T.Box3(), point = new T.Vector3(), offset = new T.Matrix4(), matrix = new T.Matrix4();
  let unavailable = false;
  root.traverseVisible(object => {
    const geometry = object.geometry, positions = geometry?.attributes.position;
    if (!positions) return;
    if (object.isSkinnedMesh) {
      let cached = cache.get(object);
      const key = boundsKey(object);
      if (!cached || cached.key !== key) {
        const record = sharedBoundsRecord(object);
        if (!allowBuild && !record.done) { unavailable = true; return; }
        cached = record.done ? record : makeBoneInfluenceBounds(object); cache.set(object, cached);
      }
      if (cached.boxes) {
        let hasMorph = false;
        delta.min.set(0, 0, 0); delta.max.set(0, 0, 0);
        for (let target = 0; target < (cached.morphBoxes?.length ?? 0); target++) {
          const weight = object.morphTargetInfluences?.[target] ?? 0;
          if (!Number.isFinite(weight)) { unavailable = true; return; }
          if (!weight) continue;
          const box = cached.morphBoxes[target];
          if (box.isEmpty()) continue;
          delta.min.addScaledVector(weight > 0 ? box.min : box.max, weight);
          delta.max.addScaledVector(weight > 0 ? box.max : box.min, weight);
          hasMorph = true;
        }
        offset.multiplyMatrices(object.matrixWorld, object.bindMatrixInverse);
        for (let index = 0; index < cached.boxes.length; index++) {
          const box = cached.boxes[index]; if (box.isEmpty()) continue;
          morphed.copy(box);
          if (hasMorph) {
            // A linear transform of the geometry-space delta envelope encloses
            // every morph displacement assigned to this bone. Adding AABBs is
            // conservative even with negative or simultaneously active weights.
            boneDelta.copy(delta).applyMatrix4(cached.deltaTransforms[index]);
            morphed.min.add(boneDelta.min); morphed.max.add(boneDelta.max);
          }
          matrix.multiplyMatrices(offset, object.skeleton.bones[index].matrixWorld);
          result.union(transformed.copy(morphed).applyMatrix4(matrix));
        }
        return;
      }
    }
    if (object.isSkinnedMesh || geometry.morphAttributes.position?.length) {
      if (!allowBuild) { unavailable = true; return; }
      for (let vertex = 0; vertex < positions.count; vertex++) result.expandByPoint(object.getVertexPosition(vertex, point).applyMatrix4(object.matrixWorld));
    } else {
      if (!geometry.boundingBox) geometry.computeBoundingBox();
      result.union(transformed.copy(geometry.boundingBox).applyMatrix4(object.matrixWorld));
    }
  });
  return unavailable ? null : result;
}
function precisePortalBounds(root, portal, cache, allowBuild = true) {
  return visualWorldBounds(root, cache, {allowBuild})?.applyMatrix4(frame(portal).invert()) ?? null;
}
function overlapsAperture(box, source, target) {
  const {width, height} = dimensions(source, target);
  return !box.isEmpty() && box.min.z < .004 && box.max.z > -.004 && box.max.x > -width / 2 && box.min.x < width / 2 && box.max.y >= 0 && box.min.y < height;
}
function copyPose(presentation, targetScene = null, source = null, target = null) {
  for (const [original, copy] of presentation.nodes) {
    if (original !== presentation.root) {
      copy.position.copy(original.position); copy.quaternion.copy(original.quaternion); copy.scale.copy(original.scale);
      copy.matrix.copy(original.matrix); copy.matrixAutoUpdate = original.matrixAutoUpdate;
    }
    copy.visible = original.visible;
    copy.renderOrder = original.renderOrder;
    copy.layers.mask = original.layers.mask;
    copy.castShadow = original.castShadow; copy.receiveShadow = original.receiveShadow;
    if (original.morphTargetInfluences) copy.morphTargetInfluences = [...original.morphTargetInfluences];
  }
  updateSourceMatrices(presentation.root);
  targetScene?.updateWorldMatrix(true, false);
  presentation.ghost.matrixAutoUpdate = false;
  presentation.ghost.matrix.copy(targetScene ? targetScene.matrixWorld : new T.Matrix4()).invert();
  presentation.ghost.matrix.multiply(source && target ? portalObjectMatrix(presentation.root.matrixWorld, source, target) : presentation.root.matrixWorld);
  presentation.ghost.matrix.decompose(presentation.ghost.position, presentation.ghost.quaternion, presentation.ghost.scale);
  presentation.ghost.matrixWorldNeedsUpdate = true;
  presentation.ghost.updateMatrixWorld(true);
  for (const skeleton of presentation.skeletons) skeleton.update();
}
function createPresentation(root, {deferMaterials = false} = {}) {
  const ghost = cloneSkeleton(root), nodes = [], meshes = [], materials = new Map(), skeletons = new Set();
  ghost.name = `Portal continuation · ${root.name || root.uuid}`;
  ghost.userData.portalContinuation = true;
  ghost.userData.portalContinuationSourceUuid = root.uuid;
  function materialPair(original) {
    if (!materials.has(original)) materials.set(original, {original, source: deferMaterials ? null : cloneMaterial(original), target: deferMaterials ? null : cloneMaterial(original)});
    return materials.get(original);
  }
  function visit(original, copy) {
    nodes.push([original, copy]);
    if (original.material) {
      const bases = materialList(original.material), pairs = bases.map(materialPair);
      meshes.push({original, copy, pairs, baseMaterial: original.material, sourceMaterial: Array.isArray(original.material) ? pairs.map(item => item.source) : pairs[0].source, targetMaterial: Array.isArray(original.material) ? pairs.map(item => item.target) : pairs[0].target});
      if (!deferMaterials) copy.material = meshes.at(-1).targetMaterial;
      copy.frustumCulled = false; // animated clone bounds must never be stale
    }
    if (copy.isSkinnedMesh) skeletons.add(copy.skeleton);
    for (let i = 0; i < original.children.length; i++) visit(original.children[i], copy.children[i]);
  }
  visit(root, ghost);
  return {root, ghost, nodes, meshes, materials, skeletons, bindings: new Map(meshes.map(mesh => [mesh.original, mesh])), active: false, pending: 0, disposeRequested: false, warmedKeys: new Set(), warmupReadyKeys: new Set(), warmups: new Map()};
}
function restoreSource(presentation) {
  for (const mesh of presentation.meshes) if (mesh.original.material === mesh.sourceMaterial) mesh.original.material = mesh.baseMaterial;
  presentation.active = false;
  presentation.ghost.removeFromParent();
}
function destroyPresentation(presentation) {
  restoreSource(presentation);
  presentation.disposeRequested = true;
  if (presentation.pending) return;
  for (const pair of presentation.materials.values()) { pair.source?.dispose(); pair.target?.dispose(); }
  for (const skeleton of presentation.skeletons) skeleton.dispose();
  presentation.materials.clear(); presentation.skeletons.clear();
}
function syncAppearance(presentation, clips) {
  for (const pair of presentation.materials.values()) {
    copyMaterialState(pair.source, pair.original); copyMaterialState(pair.target, pair.original);
    applyClip(pair.source, clips.source); applyClip(pair.target, clips.target);
  }
}
function materialWarmupKey(sourceView, targetView, clips) {
  const scenes = [sourceView, targetView].map(view => `${view.scene.uuid}:${view.world?.revision ?? ''}`).sort();
  return `${scenes.join('|')}:${clips.source.planes.length}`;
}
function prewarm(presentation, renderer, camera, sourceScene, targetScene, key) {
  if (!renderer?.compileAsync || !camera || presentation.warmedKeys.has(key)) return;
  presentation.warmedKeys.add(key);
  // compileAsync(root,camera,targetScene) uses target lighting without requiring
  // the root to be displayed. There is one pose and no second animation mixer.
  for (const side of ['source', 'target']) {
    for (const mesh of presentation.meshes) mesh.copy.material = side === 'source' ? mesh.sourceMaterial : mesh.targetMaterial;
    let pending;
    try { pending = renderer.compileAsync(presentation.ghost, camera, side === 'source' ? sourceScene : targetScene); } catch { continue; }
    presentation.pending++;
    Promise.resolve(pending).catch(() => {}).finally(() => {
      presentation.pending--;
      if (presentation.disposeRequested && !presentation.pending) destroyPresentation(presentation);
    });
  }
  for (const mesh of presentation.meshes) mesh.copy.material = mesh.targetMaterial;
}

/** Conservative retention accounting, not a driver/GPU memory measurement.
 * Include shared geometry/textures too (deduplicated), both clipped material
 * sides, both skeletons, object/bounds bookkeeping and mip/CPU image allowance.
 * Unknown texture shapes and excessive topology fail closed instead of growing
 * a cache whose cost cannot be bounded. The eight-entry limit is independent. */
function retainedBytes(handles) {
  const objects = new Set(), materials = new Set(), geometries = new Set(), textures = new Set(), buffers = new Set(), skeletons = new Set();
  let bytes = 0, invalid = false;
  const buffer = value => { const array = value?.array ?? value?.data?.array ?? value; if (ArrayBuffer.isView(array) && !buffers.has(array.buffer)) { buffers.add(array.buffer); bytes += array.buffer.byteLength * 2; } };
  const texture = value => {
    if (!value?.isTexture || textures.has(value)) return;
    textures.add(value);
    const image = value.image, width = image?.width, height = image?.height;
    if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height) || width < 1 || height < 1 || width > 8192 || height > 8192 || Array.isArray(image)) { invalid = true; return; }
    // Four-byte RGBA floor plus upload/mips/decoded-image headroom; float data
    // uses its actual wider byte width. No render targets are retained here.
    bytes += Math.max(width * height * 4, image.data?.byteLength ?? 0) * 4;
  };
  const material = value => { if (!value || materials.has(value)) return; materials.add(value); bytes += 8192; for (const item of Object.values(value)) texture(item); };
  const visit = object => {
    if (objects.has(object)) return;
    objects.add(object); bytes += 2048;
    const geometry = object.geometry;
    if (geometry && !geometries.has(geometry)) {
      geometries.add(geometry); bytes += 1024;
      for (const attr of Object.values(geometry.attributes)) buffer(attr);
      buffer(geometry.index);
      for (const attrs of Object.values(geometry.morphAttributes ?? {})) for (const attr of attrs) buffer(attr);
    }
    for (const item of object.material ? materialList(object.material) : []) material(item);
    const skeleton = object.skeleton;
    if (skeleton && !skeletons.has(skeleton)) { skeletons.add(skeleton); buffer(skeleton.boneMatrices); texture(skeleton.boneTexture); bytes += skeleton.bones.length * 4096; }
  };
  for (const handle of handles) {
    const presentation = handle.presentation;
    if (!presentation || presentation.warmups.size > 128 || presentation.warmedKeys.size > 128 || presentation.warmupReadyKeys.size > 128) return Infinity;
    handle.root.traverse(visit); presentation.ghost.traverse(visit);
    for (const pair of presentation.materials.values()) { material(pair.original); material(pair.source); material(pair.target); }
    bytes += (presentation.warmups.size + presentation.warmedKeys.size + presentation.warmupReadyKeys.size) * 1024;
  }
  return invalid || objects.size > 16384 || materials.size > 2048 || skeletons.size > 256 || textures.size > 256 ? Infinity : bytes;
}

/** Visual continuity only. Track player/remote avatars or ordinary rigid scene
 * objects; the authoritative position, ownership and physics are never changed.
 * Call sync after source animation updates and before all portal/main renders.
 */
export function createPortalStraddling({getWorldView, ensureWorldView, invalidateShadows = () => {}, renderer, getCamera = () => null, onUnsupported = () => {}, deferPreparation = false, scheduleIdle, preparationBudgetMs = 2, warmupObject} = {}) {
  const handles = new Map(), preparationQueue = createPreparationQueue({scheduleIdle, budgetMs: preparationBudgetMs});
  let disposed = false, parkedHandle = null, parkedMaterials = [];
  const retainedRemotes = new Map();
  function clearRetained(handle) {
    const listeners = retainedRemotes.get(handle);
    if (!listeners) return;
    for (const [material, listener] of listeners) material.removeEventListener('dispose', listener);
    retainedRemotes.delete(handle);
  }
  function clearParked() {
    parkedHandle = null;
    for (const [material, listener] of parkedMaterials) material.removeEventListener('dispose', listener);
    parkedMaterials = [];
  }
  function release(handle) {
    if (parkedHandle === handle) clearParked();
    clearRetained(handle);
    handle.cancelled = true;
    if (handle.presentation) destroyPresentation(handle.presentation);
    if (handle.activePair) { invalidateShadows(handle.worldId); invalidateShadows(handle.activePair.world.id); }
  }
  function admit(descriptor) {
    const {id, root, world} = descriptor;
    let handle = handles.get(id);
    if (handle && (handle.root !== root || handle.modelRevision !== root.userData.avatarModelRevision || layoutChanged(handle))) { release(handle); handles.delete(id); handle = null; }
    if (!handle) {
      const layout = captureLayout(root);
      handle = {root, layout, modelRevision: root.userData.avatarModelRevision, boundsCache: new WeakMap(), radius: broadRadius(root, descriptor.radius), worldId: world.id, presentation: null, activePair: null, side: 1, supported: supportedRoot(root), hasSkin: layout.some(item => item.object.isSkinnedMesh), prepared: false, cancelled: false, preparing: null};
      handles.set(id, handle);
      if (!handle.supported) onUnsupported(id, 'Straddling supports ordinary Mesh/SkinnedMesh materials without existing clipping or custom depth shaders.');
    }
    if (parkedHandle === handle) clearParked();
    clearRetained(handle);
    return handle;
  }
  async function prepareHandle(handle) {
    if (!handle.supported || handle.cancelled || handle.prepared) return handle.prepared;
    if (handle.preparing) return handle.preparing;
    handle.preparing = (async () => {
      let presentation = handle.presentation;
      if (!presentation) {
        await preparationQueue.add(() => { if (!handle.cancelled) presentation = createPresentation(handle.root, {deferMaterials: true}); return true; });
        if (!presentation) return false;
        await Promise.all([...presentation.materials.values()].map(pair => preparationQueue.add(() => {
          if (!handle.cancelled) { pair.source = cloneMaterial(pair.original); pair.target = cloneMaterial(pair.original); }
          return true;
        })));
        if (handle.cancelled) { destroyPresentation(presentation); return false; }
        for (const mesh of presentation.meshes) {
          mesh.sourceMaterial = Array.isArray(mesh.baseMaterial) ? mesh.pairs.map(pair => pair.source) : mesh.pairs[0].source;
          mesh.targetMaterial = Array.isArray(mesh.baseMaterial) ? mesh.pairs.map(pair => pair.target) : mesh.pairs[0].target;
          mesh.copy.material = mesh.targetMaterial;
        }
        handle.presentation = presentation;
      }
      const pending = [];
      for (const item of handle.layout) if (item.object.isSkinnedMesh) {
        const mesh = item.object, record = sharedBoundsRecord(mesh);
        if (!record.done && !record.promise) record.promise = preparationQueue.add(() => record.step(256));
        if (record.promise) pending.push(record.promise);
      }
      await Promise.all(pending);
      if (handle.cancelled) return false;
      for (const item of handle.layout) if (item.object.isSkinnedMesh) {
        const record = sharedBoundsRecord(item.object);
        handle.boundsCache.set(item.object, record);
      }
      // Exercise the first pose/bone transform traversal before proximity too.
      // It can otherwise trigger a separate cold JS/rig cost at the threshold.
      await preparationQueue.add(() => { if (!handle.cancelled && !presentation.active) copyPose(presentation); return true; });
      await preparationQueue.add(() => { if (!handle.cancelled) visualWorldBounds(handle.root, handle.boundsCache, {allowBuild: false}); return true; });
      if (handle.cancelled) return false;
      handle.prepared = true;
      return true;
    })().finally(() => { handle.preparing = null; });
    return handle.preparing;
  }
  function queueWarmup(handle, pair, sourceView, targetView, side = 1) {
    const presentation = handle.presentation;
    if (!presentation || handle.cancelled || handle === parkedHandle || retainedRemotes.has(handle)) return Promise.resolve();
    const clips = straddlingClipVolumes(pair.source, pair.target, side);
    // Warmup covers BOTH material sides in BOTH scene light setups, so the
    // reciprocal crossing must reuse it rather than launch more warmup draws.
    const key = materialWarmupKey(sourceView, targetView, clips);
    if (presentation.warmups.has(key)) return presentation.warmups.get(key);
    if (presentation.warmedKeys.has(key)) return Promise.resolve();
    if (!presentation.active) syncAppearance(presentation, clips);
    if (!warmupObject) {
      const pending = preparationQueue.add(() => {
        if (!handle.cancelled) prewarm(presentation, renderer, getCamera(), sourceView.scene, targetView.scene, key);
        return true;
      });
      presentation.warmups.set(key, pending);
      return pending;
    }
    presentation.warmedKeys.add(key);
    presentation.pending++;
    const withMaterials = (which, callback) => {
      const prior = presentation.meshes.map(mesh => mesh.copy.material), priorClips = [];
      for (const pair of which === 'base' ? [] : presentation.materials.values()) {
        const material = pair[which];
        priorClips.push([material, {planes: material.clippingPlanes, intersection: material.clipIntersection}]);
        applyClip(material, clips[which]);
      }
      for (const mesh of presentation.meshes) mesh.copy.material = which === 'base' ? mesh.baseMaterial : which === 'source' ? mesh.sourceMaterial : mesh.targetMaterial;
      try { return callback(); } finally {
        presentation.meshes.forEach((mesh, i) => { mesh.copy.material = prior[i]; });
        for (const [material, volume] of priorClips) applyClip(material, volume);
      }
    };
    const pending = Promise.resolve().then(() => warmupObject({object: presentation.ghost, sourceScene: sourceView.scene, targetScene: targetView.scene, withMaterials, isValid: () => !handle.cancelled && !presentation.disposeRequested})).catch(() => {}).finally(() => {
      presentation.pending--;
      presentation.warmupReadyKeys.add(key);
      if (presentation.disposeRequested && !presentation.pending) destroyPresentation(presentation);
    });
    presentation.warmups.set(key, pending);
    return pending;
  }
  async function prepare(descriptors, worlds) {
    if (disposed) return;
    await Promise.all(descriptors.map(async descriptor => {
      if (!descriptor.id || !descriptor.root || !descriptor.world) return;
      const handle = admit(descriptor);
      if (!await prepareHandle(handle) || disposed || handle === parkedHandle || retainedRemotes.has(handle)) return;
      const sourceView = getWorldView(descriptor.world.id); if (!sourceView) return;
      const requests = [];
      for (const source of selectedPortals(descriptor.world, descriptor.activePortalId)) {
        const pair = resolvePortalPair(descriptor.world, source, worlds), targetView = pair && getWorldView(pair.world.id);
        if (pair && targetView) requests.push(queueWarmup(handle, pair, sourceView, targetView));
      }
      await Promise.all(requests);
    }));
  }
  function sync(descriptors, worlds) {
    if (disposed) return {states: [], straddlingCount: 0, pinnedWorldIds: []};
    const seen = new Set(), states = [], pinned = new Set();
    for (const descriptor of descriptors) {
      const {id, root, world, activePortalId} = descriptor;
      if (!id || !root || !world) continue;
      seen.add(id);
      const handle = admit(descriptor);
      if (!handle.supported) continue;
      const traversalKey=descriptor.portalTraversal?JSON.stringify(descriptor.portalTraversal):null;
      if(handle.traversalKey!==traversalKey || descriptor.portalTraversal?.targetWorldId!==world.id)handle.completedTraversalSequence=null;
      handle.traversalKey=traversalKey;
      if (deferPreparation && handle.hasSkin && !handle.prepared && !handle.backgroundPrepare) {
        handle.backgroundPrepare = true;
        // Prepare newly seen skins before proximity, including shaders for
        // already-loaded direct neighbors. Never create extra scenes here.
        void prepare([descriptor], worlds).catch(() => {});
      }
      updateSourceMatrices(root);
      const rootPosition = new T.Vector3().setFromMatrixPosition(root.matrixWorld), scale = new T.Vector3().setFromMatrixScale(root.matrixWorld);
      const radius = handle.radius * Math.max(Math.abs(scale.x), Math.abs(scale.y), Math.abs(scale.z));
      let candidate = null, nearest = Infinity;
      for (const source of selectedPortals(world, activePortalId)) {
        const pair = resolvePortalPair(world, source, worlds); if (!pair) continue;
        const allowed = descriptor.allowedTargetWorldIds;
        if (allowed && !(allowed instanceof Set ? allowed.has(pair.world.id) : allowed.includes(pair.world.id))) continue;
        const local = portalToLocal(rootPosition, source), shape = dimensions(source, pair.target);
        const distance = Math.abs(local.z);
        if (distance > radius + 1.5 || Math.abs(local.x) > shape.width / 2 + radius || local.y < -radius || local.y > shape.height + radius) continue;
        if (distance < nearest) { nearest = distance; candidate = {...pair, local}; }
      }
      if (!candidate) {
        if(descriptor.portalTraversal?.targetWorldId===world.id)handle.completedTraversalSequence=descriptor.portalTraversal.sequence;
        if (handle.presentation?.active) { restoreSource(handle.presentation); invalidateShadows(handle.worldId); invalidateShadows(handle.activePair?.world.id); }
        handle.activePair = null; handle.worldId = world.id; continue;
      }
      const targetView = getWorldView(candidate.world.id) ?? (descriptor.allowSceneCreation === false ? null : ensureWorldView?.(candidate.world));
      const sourceView = getWorldView(world.id);
      if (!targetView?.scene || !sourceView?.scene) {
        if (handle.presentation?.active) { restoreSource(handle.presentation); invalidateShadows(handle.worldId); invalidateShadows(handle.activePair?.world.id); }
        handle.activePair = null; handle.worldId = world.id; continue;
      }
      pinned.add(world.id); pinned.add(candidate.world.id);
      const prior = handle.activePair;
      const samePair = prior && ((handle.worldId === world.id && prior.source.id === candidate.source.id && prior.target.id === candidate.target.id) || (handle.worldId === candidate.world.id && prior.source.id === candidate.target.id && prior.target.id === candidate.source.id));
      const receipt=descriptor.portalTraversal;
      if(receipt?.targetWorldId===world.id && (receipt.sourceWorldId!==candidate.world.id || receipt.targetPortalId!==candidate.source.id || receipt.sourcePortalId!==candidate.target.id))handle.completedTraversalSequence=receipt.sequence;
      const completing=receipt && handle.completedTraversalSequence!==receipt.sequence && receipt.targetWorldId===world.id && receipt.sourceWorldId===candidate.world.id && receipt.targetPortalId===candidate.source.id && receipt.sourcePortalId===candidate.target.id;
      const side = completing ? receipt.targetSide : (samePair && handle.worldId===world.id ? handle.side : Math.sign(candidate.local.z) || 1);
      const approach=descriptor.portalApproach;
      const targetSide=approach?.portalId===candidate.source.id&&approach.sourceSide===side&&[1,-1].includes(approach.targetSide)?approach.targetSide:portalDestinationSide(world,candidate,side,receipt);
      const sourceFrame=portalEntryFrame(candidate.source,side),targetFrame=portalEntryFrame(candidate.target,targetSide);
      const clips = straddlingClipVolumes(sourceFrame, targetFrame, 1);
      if (!handle.presentation) {
        if (deferPreparation) { void prepareHandle(handle).catch(() => {}); continue; }
        handle.presentation = createPresentation(root);
      }
      const presentation = handle.presentation;
      if (deferPreparation && !handle.prepared) continue;
      syncAppearance(presentation, clips);
      copyPose(presentation, targetView.scene, sourceFrame, targetFrame);
      const warmKey = `${world.id}:${candidate.source.id}:${candidate.world.revision}:${clips.source.planes.length}`;
      if (deferPreparation) void queueWarmup(handle, {...candidate,source:sourceFrame,target:targetFrame}, sourceView, targetView, 1);
      else prewarm(presentation, renderer, getCamera(), sourceView.scene, targetView.scene, warmKey);
      if (deferPreparation && warmupObject && !presentation.warmupReadyKeys.has(materialWarmupKey(sourceView, targetView, clips))) {
        // Do not introduce an uncompiled clipping program into a live draw.
        // A just-arrived remote stays whole in its owning world until ready.
        if (presentation.active) { restoreSource(presentation); invalidateShadows(handle.worldId); invalidateShadows(handle.activePair?.world.id); }
        handle.activePair = null; handle.worldId = world.id; continue;
      }
      // A root-space sphere is checked above. Nearby skins use cached bone
      // influence/morph envelopes; deferred sync never scans skin vertices.
      const bounds = nearest <= radius ? precisePortalBounds(root, candidate.source, handle.boundsCache, !deferPreparation) : null;
      const active = bounds ? overlapsAperture(bounds, candidate.source, candidate.target) : deferPreparation && nearest <= radius;
      if (active) {
        const changed = !presentation.active || !samePair || presentation.ghost.parent !== targetView.scene || handle.worldId !== world.id;
        for (const mesh of presentation.meshes) mesh.original.material = mesh.sourceMaterial;
        targetView.scene.add(presentation.ghost); presentation.ghost.updateMatrixWorld(true);
        presentation.active = true;
        if (changed) { invalidateShadows(world.id); invalidateShadows(candidate.world.id); }
        handle.activePair = candidate; handle.side = side;
        states.push({id, sourceWorldId: world.id, targetWorldId: candidate.world.id, sourcePortalId: candidate.source.id, targetPortalId: candidate.target.id, side, ghost: presentation.ghost, sourceRoot: root, clips});
      } else {
        if (presentation.active) { restoreSource(presentation); invalidateShadows(handle.worldId); invalidateShadows(handle.activePair?.world.id); }
        handle.activePair = null;
        if(completing)handle.completedTraversalSequence=receipt.sequence;
      }
      handle.worldId = world.id;
    }
    for (const [id, handle] of handles) if (!seen.has(id) && handle !== parkedHandle && !retainedRemotes.has(handle)) { release(handle); handles.delete(id); }
    return {states, straddlingCount: states.length, pinnedWorldIds: [...pinned]};
  }
  return {
    sync, prepare,
    /** Keep one prepared local continuation while another owner borrows the
     * avatar. No ghost remains displayed and no destination scene is pinned.
     * A later descriptor passes the usual root/model/layout validation before
     * reusing these materials and scene/revision warm keys. Remote removal and
     * explicit remove/dispose retain their destructive semantics.
     */
    park(id) {
      const handle = handles.get(id);
      if (disposed || !handle || handle.cancelled) return false;
      // Routine movement resets repeat while another owner has its own clipped
      // materials installed. Parking is idempotent; validate the full layout
      // only when native admission resumes and the owner has restored its base.
      if (parkedHandle === handle) {
        if (handle.modelRevision !== handle.root.userData.avatarModelRevision) { release(handle); handles.delete(id); return false; }
        return true;
      }
      // An unfinished preparation may still read the live hierarchy on its next
      // cooperative slice. Cancel it before another owner can install clipping.
      // Only completed initial work is eligible for retained reuse.
      if (!handle.prepared || handle.preparing || !handle.presentation || handle.presentation.pending || handle.modelRevision !== handle.root.userData.avatarModelRevision || layoutChanged(handle)) {
        release(handle); handles.delete(id); return false;
      }
      if (parkedHandle && parkedHandle !== handle) {
        const previous = parkedHandle;
        for (const [key, value] of handles) if (value === previous) handles.delete(key);
        release(previous);
      }
      if (handle.presentation) restoreSource(handle.presentation);
      if (handle.activePair) { invalidateShadows(handle.worldId); invalidateShadows(handle.activePair.world.id); }
      handle.activePair = null;
      clearParked(); parkedHandle = handle;
      // Avatar disposal/eviction can happen while this descriptor is omitted.
      // Keep no stale rig after an owned base resource is explicitly disposed.
      const releaseDisposed = () => { if (parkedHandle === handle) { release(handle); handles.delete(id); } };
      for (const material of handle.presentation.materials.keys()) {
        material.addEventListener('dispose', releaseDisposed); parkedMaterials.push([material, releaseDisposed]);
      }
      return true;
    },
    /** A separate bounded city-remote owner; never evicts the borrowed local rig.
     * Only settled preparations are eligible. Missing descriptors do no work. */
    parkRemote(id) {
      const handle = handles.get(id), presentation = handle?.presentation;
      if (disposed || !handle || handle.cancelled || handle === parkedHandle) return false;
      if (retainedRemotes.has(handle)) return true;
      if (!handle.prepared || handle.preparing || !presentation || presentation.pending
        || handle.modelRevision !== handle.root.userData.avatarModelRevision || layoutChanged(handle)
        || retainedRemotes.size >= 8 || retainedBytes([...retainedRemotes.keys(), handle]) > 256 * 1024 * 1024) return false;
      restoreSource(presentation);
      if (handle.activePair) { invalidateShadows(handle.worldId); invalidateShadows(handle.activePair.world.id); }
      handle.activePair = null; handle.traversalKey = null; handle.completedTraversalSequence = null;
      const listeners = [], releaseDisposed = () => { if (retainedRemotes.has(handle)) { release(handle); handles.delete(id); } };
      retainedRemotes.set(handle, listeners);
      for (const material of presentation.materials.keys()) { material.addEventListener('dispose', releaseDisposed); listeners.push([material, releaseDisposed]); }
      return true;
    },
    suspend(id) {const handle=handles.get(id);if(handle?.presentation)restoreSource(handle.presentation);},
    /** Promote a prepared detached avatar without rebuilding its ghost. Only
     * explicitly replaced root/body/extra containers may change identity; all
     * skin, geometry, base materials and hierarchy must still match exactly.
     * This does not advance a mixer or change a source pose/physical root.
     */
    rebindPreparedRoot(id,root,replacements=new Map()) {
      const handle=handles.get(id),presentation=handle?.presentation;
      if(disposed||!handle?.prepared||handle.cancelled||!presentation||presentation.active||presentation.pending)throw Error('Inactive prepared native presentation required');
      if(replacements.get(handle.root)!==root)throw Error('Explicit prepared root substitution required');
      const nextLayout=captureLayout(root);
      if(nextLayout.length!==handle.layout.length)throw Error('Prepared avatar layout changed');
      for(let i=0;i<nextLayout.length;i++){
        const previous=handle.layout[i],next=nextLayout[i],object=replacements.get(previous.object)??previous.object;
        if(object!==next.object||previous.geometry!==next.geometry||previous.boundsVersion!==next.boundsVersion||previous.materials?.length!==next.materials?.length||next.materials?.some((material,index)=>material!==previous.materials[index]))throw Error('Prepared avatar resource/layout mismatch');
      }
      const nodes=presentation.nodes.map(([original,copy])=>[replacements.get(original)??original,copy]);
      for(const mesh of presentation.meshes)mesh.original=replacements.get(mesh.original)??mesh.original;
      presentation.nodes=nodes;presentation.root=root;presentation.ghost.userData.portalContinuationSourceUuid=root.uuid;presentation.bindings=new Map(presentation.meshes.map(mesh=>[mesh.original,mesh]));
      handle.root=root;handle.layout=nextLayout;handle.modelRevision=root.userData.avatarModelRevision;handle.activePair=null;
      return true;
    },
    isRemoteParked: id => retainedRemotes.has(handles.get(id)),
    isPrepared: id => handles.get(id)?.prepared ?? false,
    getPreparationStats: () => preparationQueue.stats(),
    remove(id) { const handle = handles.get(id); if (handle) { release(handle); handles.delete(id); } },
    getHandle: id => handles.get(id),
    dispose() { if (disposed) return; disposed = true; for (const handle of handles.values()) release(handle); handles.clear(); },
  };
}

